<template>
  <div>
    <LoadingPanel ref="loadingPanel" />
    <StaticDataTable class="static-table-list">
      <template v-slot:header>
        <tr>
          <th>Type</th>
          <th>Customer Filter</th>
          <th>Customer Label Filter</th>
          <th>Stock Filter</th>
          <th>Stock Type Filter</th>
          <th>Category Filter</th>
          <th>Price Group Filter</th>
          <th>Service Range Filter</th>
          <th>Adjustment</th>
          <th>Actions</th>
        </tr>
      </template>
      <template v-slot:body>
        <tr v-for="(updateItem, index) in modifierList" :key="index">
          <td>
            <span class="label label-pricing-sales" v-if="isItemSales(updateItem)">Sales</span>
            <span class="label label-pricing-customer" v-if="isItemCustomer(updateItem)">Customer</span>
          </td>
          <td>
            <div class="font-weight-bold" v-if="itemHasFilter(updateItem.customerFilter)">
              <Tooltip :label="itemTooltip(updateItem.customerFilter)" linkStyle>{{itemSummary(updateItem.customerFilter)}}</Tooltip>
            </div>
            <div v-if="!itemHasFilter(updateItem.customerFilter)">{{itemSummary(updateItem.customerFilter)}}</div>
          </td>
          <td>
            <div class="font-weight-bold" v-if="itemHasFilter(updateItem.customerLabelFilter)">
              <Tooltip :label="itemTooltip(updateItem.customerLabelFilter)" linkStyle>{{itemSummary(updateItem.customerLabelFilter)}}</Tooltip>
            </div>
            <div v-if="!itemHasFilter(updateItem.customerLabelFilter)">{{itemSummary(updateItem.customerLabelFilter)}}</div>
          </td>
          <td>
            <div class="font-weight-bold" v-if="itemHasFilter(updateItem.stockFilter)">
              <Tooltip :label="itemTooltip(updateItem.stockFilter)" linkStyle>{{itemSummary(updateItem.stockFilter)}}</Tooltip>
            </div>
            <div v-if="!itemHasFilter(updateItem.stockFilter)">{{itemSummary(updateItem.stockFilter)}}</div>
          </td>
          <td>
            <div class="font-weight-bold" v-if="itemHasFilter(updateItem.stockTypeFilter)">
              <Tooltip :label="itemTooltip(updateItem.stockTypeFilter)" linkStyle>{{itemSummary(updateItem.stockTypeFilter)}}</Tooltip>
            </div>
            <div v-if="!itemHasFilter(updateItem.stockTypeFilter)">{{itemSummary(updateItem.stockTypeFilter)}}</div>
          </td>
          <td>
            <div class="font-weight-bold" v-if="itemHasFilter(updateItem.categoryFilter)">
              <Tooltip :label="itemTooltip(updateItem.categoryFilter)" linkStyle>{{itemSummary(updateItem.categoryFilter)}}</Tooltip>
            </div>
            <div v-if="!itemHasFilter(updateItem.categoryFilter)">{{itemSummary(updateItem.categoryFilter)}}</div>
          </td>
          <td>
            <div class="font-weight-bold" v-if="itemHasFilter(updateItem.priceGroupFilter)">
              <Tooltip :label="itemTooltip(updateItem.priceGroupFilter)" linkStyle>{{itemSummary(updateItem.priceGroupFilter)}}</Tooltip>
            </div>
            <div v-if="!itemHasFilter(updateItem.priceGroupFilter)">{{itemSummary(updateItem.priceGroupFilter)}}</div>
          </td>
          <td>
            <div class="font-weight-bold" v-if="itemHasFilter(updateItem.serviceRangeFilter)">
              <Tooltip :label="itemTooltip(updateItem.serviceRangeFilter)" linkStyle>{{itemSummary(updateItem.serviceRangeFilter)}}</Tooltip>
            </div>
            <div v-if="!itemHasFilter(updateItem.serviceRangeFilter)">{{itemSummary(updateItem.serviceRangeFilter)}}</div>
          </td>
          <td v-html="displayItemAdjustmentInfo(updateItem)"></td>
          <td>
            <ActionDropMenu :tableAction="true" :forceDropdown="true">
              <DropdownMenuItem v-if="editing" @click="editPriceUpdateItem(index)" icon="fa-pencil">Edit</DropdownMenuItem>
              <DropdownMenuItem v-if="editing" @click="removePriceUpdateItem(index)" icon="fa-trash-alt">Remove</DropdownMenuItem>
              <DropdownMenuItem v-if="!editing" @click="displayPriceUpdateItem(index)" icon="fa-eye">Show Details</DropdownMenuItem>
            </ActionDropMenu>
          </td>
        </tr>
      </template>
      <template v-slot:empty v-if="modifierList.length == 0">
        No bulk modifiers provided
      </template>
    </StaticDataTable>
    <div v-if="editing">
      <button class="btn btn-white btn-xs" @click.prevent="addBulkModifierItem"><i class="far fa-plus"></i> Add Item</button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import _ from 'lodash'
import PriceUpdateItemDialog from '@/modules/pricing/views/priceUpdateItemDialog'

import { PriceTypes, FilterOptionTypes, BulkUpdateAdjustmentTypes, InventoryStockTypes, InventoryStockTypeLookup, PriceRequestTypes, PricingRequestStatusTypes } from '@/store/types'

export default {
  name: 'PriceBulkModifierEditor',
  model: {
    event: 'update'
  },
  props: {
    editing: Boolean,
    value: Array,
  },
  watch: {
    value: function() {
      this.modifierList = _.cloneDeep(this.value)
    }
  },
  data() {
    return {
      modifierList: _.cloneDeep(this.value),
      customerLookup: [],
      customerLabelLookup: [],
      stockLookup: [],
      stockTypeLookup: InventoryStockTypeLookup.filter(type => type.value != InventoryStockTypes.template),   // Don't include template types
      categoryLookup: [],
      priceGroupLookup: [],
      serviceRangeLookup: [],

      updateTypeLookup: [
        { value: PriceTypes.sales, name: 'Sales' },
        { value: PriceTypes.customer, name: 'Customer' },
      ],
      adjustmentTypeLookup: [
        { value: BulkUpdateAdjustmentTypes.increasePercent, name: 'Increase Percentage' },
        { value: BulkUpdateAdjustmentTypes.decreasePercent, name: 'Decrease Percentage' },
        { value: BulkUpdateAdjustmentTypes.increaseAmount, name: 'Increase Amount' },
        { value: BulkUpdateAdjustmentTypes.decreaseAmount, name: 'Decrease Amount' }
      ],      
    }
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      this.$refs.loadingPanel.startLoading()

      // Get all the lookup data
      Promise.all([
        this.$apiService.contactService.getContactList({
          isActive: 1,
          isCustomer: 1
        }),
        this.$apiService.inventoryService.getStockList(),
        this.$apiService.inventoryService.getStockCategoryList(),
        this.$apiService.pricingService.getStockPriceGroupList(),
        this.$apiService.pricingService.getServicePriceRangeList(),
        this.$apiService.coreService.getLabelsForType('contact')
      ])
      .then(results => {
        this.customerLookup = results[0].data.map(item => {
          return { value: item.id, name: item.name }
        })
        this.stockLookup = results[1].data.filter(item => item.isActive).map(item => {
          return { value: item.id, name: item.code }
        })
        this.categoryLookup = results[2].data.filter(item => item.isActive).map(item => {
          return { value: item.id, name: item.name }
        })
        this.priceGroupLookup = results[3].data.filter(item => item.isActive).map(item => {
          return { value: item.id, name: item.name }
        })
        this.serviceRangeLookup = results[4].data.filter(item => item.isActive).map(item => {
          var name = item.name

          // If there are multiple ranges with same name, show price group info in name
          if(item.stockPriceGroups && item.stockPriceGroups.length > 0 && results[4].data.find(otherRange => otherRange.isActive && otherRange.id != item.id && otherRange.name == item.name)) {
            name += ' (' + item.stockPriceGroups.map(group => group.name).join(', ') + ')'
          }

          return { value: item.id, name: name }
        })

        this.customerLabelLookup = results[5].data.map(item => {
          return { value: item.id, name: item.name, colour: item.colour }
        })
      })
      .catch(err => {
        console.error(err)
        this.$refs.loadingPanel.setError('Error loading data', err)
      })
      .finally(() => {
        this.$refs.loadingPanel.finishedLoading()
      })
    },

    isItemSales(item) {
      return item.priceType == PriceTypes.sales
    },
    isItemCustomer(item) {
      return item.priceType == PriceTypes.customer
    },
    itemHasFilter(filterInfo) {
      let filterCount = (filterInfo.valueList || []).length
      return filterInfo.type != FilterOptionTypes.all && filterCount > 0
    },
    itemSummary(filterInfo) {
      let filterCount = (filterInfo.valueList || []).length

      if(filterInfo.type == FilterOptionTypes.all)
        return 'All'
      else if(filterInfo.type == FilterOptionTypes.include)
        return `Include ${filterCount} item${filterCount == 1 ? '' : 's'}`
      else if(filterInfo.type == FilterOptionTypes.exclude)
        return `Exclude ${filterCount} item${filterCount == 1 ? '' : 's'}`

      return 'All'
    },
    itemTooltip(filterInfo) {
      let filterCount = (filterInfo.valueList || []).length
      let descList = filterInfo.descriptionList || []
      let maxDisplay = 4
      var displayDetails = descList.slice(0, maxDisplay).join(', ')
      if(descList.length > maxDisplay)
        displayDetails += ` + ${descList.length - maxDisplay} more`

      if(filterInfo.type == FilterOptionTypes.all)
        return 'All'
      else if(filterInfo.type == FilterOptionTypes.include)
        return `Includes ${displayDetails}`
      else if(filterInfo.type == FilterOptionTypes.exclude)
        return `Excludes ${displayDetails}`

      return 'All'
    },
    displayItemAdjustmentInfo(item) {
      var isPercent = item.adjustmentType == BulkUpdateAdjustmentTypes.increasePercent || item.adjustmentType == BulkUpdateAdjustmentTypes.decreasePercent
      var isAmount = item.adjustmentType == BulkUpdateAdjustmentTypes.increaseAmount || item.adjustmentType == BulkUpdateAdjustmentTypes.decreaseAmount
      var isPositive = item.adjustmentType == BulkUpdateAdjustmentTypes.increasePercent || item.adjustmentType == BulkUpdateAdjustmentTypes.increaseAmount

      var value = parseFloat(item.adjustmentValue)

      let className = value == 0 ? '' : (isPositive ? 'financial-profit' : 'financial-loss')

      if(value == 0)
        return ''

      if(isAmount)      
        return `<span class="font-weight-bold ${className}">${isPositive ? '+' : '-'}` + this.$options.filters.formatCurrency(value) + `</span>`
      return `<span class="font-weight-bold ${className}">${isPositive ? '+' : '-'}` + this.$options.filters.formatPercent(value) + `</span>`
    },
    displayPriceUpdateItem(index) {
      this.displayModal(PriceUpdateItemDialog, { 
        isCreateMode: false,
        editing: false,
        lookupDetails: this.getItemDialogLookup(),
        updateItem: this.$data.modifierList[index]
      })
    },
    editPriceUpdateItem(index) {
      this.displayModal(PriceUpdateItemDialog, { 
        isCreateMode: false,
        editing: true,
        lookupDetails: this.getItemDialogLookup(),
        updateItem: this.$data.modifierList[index]
      })
      .then((priceItem) => {
        if(priceItem) {
          Vue.set(this.modifierList, index, priceItem)

          this.$emit('update', this.modifierList)
        }
      })
    },
    removePriceUpdateItem(index) {
      this.$data.modifierList.splice(index, 1)
      this.$emit('update', this.modifierList)
    },
    getItemDialogLookup() {
      return {
        customerLookup: this.$data.customerLookup,
        customerLabelLookup: this.$data.customerLabelLookup,
        stockLookup: this.$data.stockLookup,
        categoryLookup: this.$data.categoryLookup,
        priceGroupLookup: this.$data.priceGroupLookup,
        serviceRangeLookup: this.$data.serviceRangeLookup,
      }
    },

    addBulkModifierItem() {
      this.displayModal(PriceUpdateItemDialog, { 
        isCreateMode: true,
        editing: true,
        lookupDetails: this.getItemDialogLookup()
      })
      .then((priceItem) => {
        if(priceItem) {
          this.modifierList.push(_.cloneDeep(priceItem))

          this.$emit('update', this.modifierList)
        }
      })
    }
  }
}
</script>